import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { getIcon } from '@/lib/iconMap';
import { SERVICES_PAGE } from '@/constants/testIds';
import { useTilt } from '@/lib/useTilt';

export const ServiceCard = ({ service, className = '' }) => {
  const Icon = getIcon(service.icon_name);
  const videoRef = useRef(null);
  const tilt = useTilt(6);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseMove = (e) => {
    tilt.onMouseMove(e);
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    tilt.onMouseLeave();
  };

  return (
    <Link
      ref={tilt.ref}
      to={`/services/${service.slug}`}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ ...tilt.style, transition: 'transform 220ms ease-out' }}
      className={`group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6 sm:p-8 min-h-[220px] transition-colors duration-300 hover:border-[hsl(var(--lxra-gold))]/50 will-change-transform ${className}`}
      data-testid={SERVICES_PAGE.card(service.slug)}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        {service.hero_video_url ? (
          <video
            ref={videoRef}
            src={service.hero_video_url}
            poster={service.hero_image_url}
            muted
            loop
            playsInline
            preload="none"
            className="h-full w-full object-cover"
            data-testid={`${SERVICES_PAGE.card(service.slug)}-preview-video`}
          />
        ) : (
          <img src={service.hero_image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-[#0A0A0A]/85" />
      </div>
      <div className="relative z-10">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="font-display text-2xl tracking-[0.04em] text-[hsl(var(--lxra-fg))]">{service.name.toUpperCase()}</h3>
        <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{service.short_description}</p>
      </div>
      <div className="relative z-10 mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))] opacity-90 group-hover:opacity-100">
        View Service
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </div>
    </Link>
  );
};
